import { Box, Grid, Tooltip, Typography } from "@material-ui/core";
import {
  PrunedAMCScheduledReport,
  ReportRunStatus,
} from "~/typedef/amc/scheduledReport";
import styled, { css } from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useDeleteAMCScheduledReportMutation,
  useReportTypesQuery,
  useScheduledReportsQuery,
} from "~/store/mystore/amc.redux";

import AlertCard from "~/components/alert/alertCard";
import ConfirmDialog from "~/components/dialogs/confirmDialog";
import DeleteIcon from "@material-ui/icons/Delete";
import { GenericSelect } from "~/components/select/genericSelect";
import PageBlock from "~/components/containers/sideNavPageBlock";
import { PaginationArgs } from "~/typedef/pagination";
import Panel from "~/components/panel/panel";
import React from "react";
import Table from "~/components/adTable/table";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { get } from "lodash";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTypedSelector } from "~/hooks/useTypedSelector";

const PAGE_SIZE = 20;

const formatDate = (dateString: string) => {
  return moment(dateString).format("MMM D, YYYY");
};

// Styled components for the ScheduledReportCell
const ReviewContainer = styled(Grid)`
  min-width: 150px;
`;

const Divider = styled.div`
  height: 50%;
  width: 1px;
  background-color: ${({ theme }) => theme.palette.border.dark};
`;

const iconStyles = css`
  fill: ${({ theme }) => theme.palette.grey["500"]};
  width: 1rem;
  height: 1rem;
  margin-left: 5px;
  margin-right: 5px;
  cursor: pointer;
`;

const StyledDeleteIcon = styled(DeleteIcon)`
  ${iconStyles}
`;

const StyledViewIcon = styled(VisibilityIcon)`
  ${iconStyles}
`;

interface ScheduledReportCellProps {
  report: PrunedAMCScheduledReport;
  onDelete: (reportId: string) => void;
  onView: (reportId: string, reportType: string) => void;
}

const ScheduledReportCell = ({
  report,
  onDelete,
  onView,
}: ScheduledReportCellProps) => {
  const { t } = useTranslation();
  const { _id, reportName, queryType } = report;

  return (
    <ReviewContainer container>
      <Grid item container xs={12}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" noWrap>
            {reportName}
          </Typography>
        </Grid>
        <Grid item container xs={12} alignItems="center">
          <Grid item>
            <Tooltip title={t("common.view")}>
              <StyledViewIcon
                onClick={() => onView(_id, queryType)}
                color="primary"
                fontSize="small"
              />
            </Tooltip>
          </Grid>
          <Divider />
          <Grid item>
            <Tooltip title={t("common.delete")}>
              <StyledDeleteIcon
                onClick={() => onDelete(_id)}
                color="secondary"
                fontSize="small"
              />
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
    </ReviewContainer>
  );
};

const ActionsContainer = styled(Box)`
  padding: 4px;
  max-width: 400px;
  width: 100%;
`;

const ReportStatus = () => {
  const { t } = useTranslation();
  const store = useTypedSelector((state) =>
    get(state, "persistentAppSettings.setting.data.currentStore")
  );

  const [paginationParams, setPaginationParams] = useState<PaginationArgs>({
    pageSize: PAGE_SIZE,
    pageIndex: 0,
    sortKey: "createdAt",
    sortOrder: "desc",
  });
  const [selectedReportType, setSelectedReportType] = useState<string>("ALL");
  const [selectedStatus, setSelectedStatus] = useState<ReportRunStatus | "ALL">(
    "ALL"
  );

  const { reportTypes } = useReportTypesQuery(
    {},
    {
      selectFromResult: ({ data }) => {
        return {
          reportTypes: data?.reportTypes ?? [],
        };
      },
    }
  );

  const reportTypeOptions = useMemo(() => {
    const allOption = { name: "ALL", label: t("common.all") };
    if (!reportTypes) return [allOption];
    return [
      allOption,
      ...reportTypes.map((type) => ({
        name: type._id,
        label: t(`amc.${type.name}.label`),
      })),
    ];
  }, [reportTypes, t]);

  const statusOptions = useMemo(
    () => [
      { name: "ALL", label: t("common.all") },
      { name: ReportRunStatus.PENDING, label: t("reportStatus.pending") },
      { name: ReportRunStatus.FAILED, label: t("reportStatus.errored") },
      { name: ReportRunStatus.SUCCEEDED, label: t("reportStatus.succeeded") },
    ],
    [t]
  );

  const [deleteScheduledReport, { isLoading: isDeletingReport }] =
    useDeleteAMCScheduledReportMutation();
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);

  const handleDelete = (reportId: string) => {
    setSelectedReportId(reportId);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReportId) return;

    try {
      await deleteScheduledReport(selectedReportId);
      setIsDeleteModalVisible(false);
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage(t("reportStatus.deleteError"));
    }
  };

  const handleView = (reportId: string, reportType: string) => {
    const existingParams = new URLSearchParams(history.location.search);
    const params = new URLSearchParams({
      ...Object.fromEntries(existingParams.entries()),
      scheduleReportType: reportType,
      scheduledReport: reportId,
    });
    history.push(`/mystores/amc/viewamcreport?${params.toString()}`);
  };

  const history = useHistory();

  const { scheduledReports, total, isFetching } = useScheduledReportsQuery(
    {
      mid: store?.merchantId || "",
      queryType: selectedReportType,
      status: selectedStatus,
      paginationParams,
    },
    {
      skip: !store,
      selectFromResult: ({ data, isFetching }) => {
        return {
          scheduledReports: data?.scheduledReports ?? [],
          total: data?.total ?? 0,
          isFetching,
        };
      },
    }
  );

  const columns = useMemo(
    () => [
      {
        id: "reportName",
        Header: t("reportStatus.reportName"),
        accessor: "reportName",
        isVisible: true,
        width: 250,
        align: "left",
        divideRight: true,
        Cell: ({
          row,
        }: {
          row: {
            original: PrunedAMCScheduledReport;
          };
        }) => (
          <ScheduledReportCell
            report={row.original}
            onDelete={handleDelete}
            onView={handleView}
          />
        ),
      },
      {
        id: "createdAt",
        Header: t("reportStatus.createdOn"),
        accessor: "createdAt",
        isVisible: true,
        width: 150,
        align: "left",
        divideRight: true,
        Cell: ({ value }: { value: string }) =>
          value ? formatDate(value) : "-",
      },
      {
        id: "userId",
        Header: t("reportStatus.createdBy"),
        accessor: "createdByName",
        isVisible: true,
        width: 150,
        align: "left",
        divideRight: true,
      },
      {
        id: "queryType",
        Header: t("reportStatus.queryType"),
        accessor: "queryType",
        isVisible: true,
        width: 150,
        align: "left",
        divideRight: true,
        Cell: ({ value }: { value: string }) => {
          const reportType = reportTypes?.find((type) => type._id === value);
          return reportType ? t(`amc.${reportType.name}.label`) : value;
        },
      },
      {
        id: "reportConfig.frequency",
        Header: t("reportStatus.frequency"),
        accessor: "reportConfig.frequency",
        isVisible: true,
        width: 120,
        align: "left",
        divideRight: true,
        Cell: ({ value }: { value: string | null | undefined }) => {
          if (!value) return "-";
          switch (value) {
            case "w":
              return "WEEKLY";
            case "m":
              return "MONTHLY";
            case "d":
              return "DAILY";
            case "once":
              return "ONE OFF";
            default:
              return value.toUpperCase();
          }
        },
      },
      {
        id: "timeRange",
        Header: t("reportStatus.timeRange"),
        accessor: "reportConfig",
        isVisible: true,
        width: 150,
        align: "left",
        divideRight: true,
        Cell: ({
          value,
        }: {
          value: PrunedAMCScheduledReport["reportConfig"] | null | undefined;
        }) => {
          if (!value) return "-";
          if (value.frequency === "once") {
            return value.fromDate && value.toDate
              ? `${formatDate(
                  moment(value.fromDate).toISOString()
                )} - ${formatDate(moment(value.toDate).toISOString())}`
              : "-";
          }
          return value.currentPeriod || "-";
        },
      },
      {
        id: "lastRunDate",
        Header: t("reportStatus.lastRun"),
        accessor: "lastRunDate",
        isVisible: true,
        width: 150,
        align: "left",
        divideRight: true,
        Cell: ({ value }: { value: string }) => {
          return value ? formatDate(value) : "-";
        },
      },
      {
        id: "lastRunStatus",
        Header: t("reportStatus.status"),
        accessor: "lastRunStatus",
        isVisible: true,
        width: 120,
        align: "left",
        divideRight: true,
        Cell: ({ value }: { value: string }) => {
          return value ? value.toUpperCase() : "-";
        },
      },
      {
        id: "nextRunDate",
        Header: t("reportStatus.nextRun"),
        accessor: "nextRunDate",
        isVisible: true,
        width: 150,
        align: "left",
        divideRight: true,
        Cell: ({ value }: { value: string }) =>
          value ? formatDate(value) : "-",
      },
    ],
    [t, reportTypes]
  );

  const fetchData = useCallback(({ pageIndex, sortBy: newSortBy }) => {
    setPaginationParams((prev) => ({
      ...prev,
      pageIndex,
      sortKey: newSortBy?.[0]?.id || "createdAt",
      sortOrder: newSortBy?.[0]?.desc ? "desc" : "asc",
    }));
  }, []);

  const filterActions = (
    <ActionsContainer>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <GenericSelect
            id="report-type-select"
            title=""
            currentOption={reportTypeOptions.find(
              (opt) => opt.name === selectedReportType
            )}
            setOption={(opt) => {
              setSelectedReportType(opt.name);
              setPaginationParams((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            options={reportTypeOptions}
            placeholderLabel={t("reportStatus.selectReportType")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <GenericSelect
            id="status-select"
            title=""
            currentOption={statusOptions.find(
              (opt) => opt.name === selectedStatus
            )}
            setOption={(opt) => {
              setSelectedStatus(opt.name as ReportRunStatus);
              setPaginationParams((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            options={statusOptions}
            placeholderLabel={t("reportStatus.selectStatus")}
          />
        </Grid>
      </Grid>
    </ActionsContainer>
  );

  return (
    <PageBlock>
      <Panel
        id="report-status"
        title={t("panel.reportStatus")}
        actions={filterActions}
        content={
          <Table
            columns={columns}
            data={scheduledReports}
            loading={isFetching}
            fetchData={fetchData}
            sorting
            pagination
            pageSize={PAGE_SIZE}
            pageCount={Math.ceil(total / PAGE_SIZE)}
          />
        }
      />
      <ConfirmDialog
        open={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={handleConfirmDelete}
        title={t("reportStatus.confirmDelete")}
        disableConfirm={isDeletingReport}
        content={
          <Typography variant="body2">
            {t("reportStatus.confirmDeleteMessage")}
          </Typography>
        }
      />
      <AlertCard
        isOpen={!!errorMessage}
        type="error"
        onClose={() => setErrorMessage(null)}
      >
        {errorMessage}
      </AlertCard>
    </PageBlock>
  );
};

export default ReportStatus;
